// frontend/src/services/apiService.ts
import api, { CommonResponse } from './api'
import type { CourseCatalog } from '../types/course'

const getErrorMessage = (error: any) =>
  error?.response?.data?.message || error?.message || String(error)

type NanoManualPaymentParams = {
  cardNo: string
  cardExpYy: string
  cardExpMm: string
  cardPw: string
  cardHolderYmd: string
  goodsName: string
  reqPayAmt: string
  orderName: string
  orderTel: string
  orderEmail: string
}

export const requestNanoManualPayment = async (
  params: NanoManualPaymentParams
) => {
  try {
    const response = await api.post<CommonResponse>(
      '/api/payment/nano/manual/pay',
      params
    )
    return response
  } catch (error) {
    console.error('Error(requestNanoManualPayment): ', error)
    throw error
  }
}

export type ManualPaymentHistoryItem = {
  id: number
  goodsName: string | null
  reqPayAmt: string | null
  orderName: string | null
  orderTel: string | null
  orderEmail: string | null
  cardNoMasked: string | null
  cardHolderMasked: string | null
  resultCode: string | null
  resultMessage: string | null
  apprNo: string | null
  tranNo: string | null
  isSuccess: boolean
  responseData: string | null
  errorMessage: string | null
  createdAt: string
}

export const getNanoManualPaymentHistory = async (limit = 10) => {
  try {
    const response = await api.get<ManualPaymentHistoryItem[]>(
      '/api/payment/nano/manual/history',
      { params: { limit } }
    )
    return response
  } catch (error) {
    console.error('Error(getNanoManualPaymentHistory): ', error)
    throw error
  }
}

export type TradingBillingProduct = {
  code: string
  name: string
  amount: string
}

export type MyTradingSubscriptionInfo = {
  groupId: number
  hasBillKey: boolean
  cardNoMasked: string | null
  accessUntil: string | null
  isUnlimited: boolean
  isActive: boolean
}

export const getTradingBillingProducts = async () => {
  try {
    const response = await api.get<TradingBillingProduct[]>(
      '/api/payment/nano/my/products'
    )
    return response
  } catch (error) {
    console.error('Error(getTradingBillingProducts): ', error)
    throw error
  }
}

export const getMyTradingSubscriptionInfo = async () => {
  try {
    const response = await api.get<MyTradingSubscriptionInfo>(
      '/api/payment/nano/my/subscription-info'
    )
    return response
  } catch (error) {
    console.error('Error(getMyTradingSubscriptionInfo): ', error)
    throw error
  }
}

export const issueMyBillKey = async () => {
  try {
    const response = await api.post('/api/payment/nano/my/billkey/issue', {}, {
      responseType: 'text'
    })
    return response
  } catch (error) {
    console.error('Error(issueMyBillKey): ', error)
    throw error
  }
}

export const payMyTradingSubscription = async (params: { productCode: string }) => {
  try {
    const response = await api.post('/api/payment/nano/my/subscribe/pay', params)
    return response
  } catch (error) {
    console.error('Error(payMyTradingSubscription): ', error)
    throw error
  }
}

export const payMyTradingSubscriptionManual = async (params: {
  productCode: string
  cardNo: string
  cardExpYy: string
  cardExpMm: string
  cardPw: string
  cardHolderYmd: string
}) => {
  try {
    const response = await api.post('/api/payment/nano/my/subscribe/manual/pay', params)
    return response
  } catch (error) {
    console.error('Error(payMyTradingSubscriptionManual): ', error)
    throw error
  }
}

// 새 플로우: 구독 시작 → PG 팝업 HTML 반환
export const startMySubscription = async (productCode: string) => {
  try {
    const response = await api.post('/api/payment/nano/my/subscribe/start', { productCode }, {
      responseType: 'text'
    })
    return response
  } catch (error) {
    console.error('Error(startMySubscription): ', error)
    throw error
  }
}

export type BillingInviteProduct = {
  id: number
  productCode: string
  productName: string
  payAmount: string
  expiresAt: string
}

export const getMyBillingInvite = async (token: string) => {
  try {
    return await api.get<BillingInviteProduct>(
      `/api/payment/nano/my/invites/${encodeURIComponent(token)}`
    )
  } catch (error) {
    console.error('Error(getMyBillingInvite): ', error)
    throw error
  }
}

export const startMyInvitedSubscription = async (token: string) => {
  try {
    return await api.post(
      `/api/payment/nano/my/invites/${encodeURIComponent(token)}/start`,
      {},
      { responseType: 'text' }
    )
  } catch (error) {
    console.error('Error(startMyInvitedSubscription): ', error)
    throw error
  }
}

// 새 플로우: 구독 상태 폴링
export type SubscriptionStatus = {
  id: number
  status: 'pending' | 'active' | 'failed' | 'cancelled'
  productCode: string
  productName: string
  payAmount: string
  lastPaidAt: string | null
  nextPaymentAt: string | null
  failReason: string | null
  createdAt: string
}

export const getMySubscriptionStatus = async () => {
  try {
    const response = await api.get<SubscriptionStatus | null>('/api/payment/nano/my/subscription/status')
    return response
  } catch (error) {
    console.error('Error(getMySubscriptionStatus): ', error)
    throw error
  }
}

export type MyBillingHistoryItem = {
  id: number
  productCode: string
  productName: string
  payAmount: string
  beforeAccessUntil: string | null
  afterAccessUntil: string | null
  resultCode: string | null
  resultMessage: string | null
  apprNo: string | null
  tranNo: string | null
  isSuccess: boolean | number
  errorMessage: string | null
  createdAt: string
}

export const getMyBillingHistory = async (limit = 20) => {
  try {
    const response = await api.get<MyBillingHistoryItem[]>('/api/payment/nano/my/billing/history', {
      params: { limit }
    })
    return response
  } catch (error) {
    console.error('Error(getMyBillingHistory): ', error)
    throw error
  }
}

// ===== 관리자 정기결제 관리 =====

export type AdminBillingHistoryItem = {
  id: number
  userId: number
  username: string | null
  account: string | null
  productCode: string
  productName: string
  payAmount: string
  beforeAccessUntil: string | null
  afterAccessUntil: string | null
  resultCode: string | null
  resultMessage: string | null
  apprNo: string | null
  tranNo: string | null
  isSuccess: boolean | number
  errorMessage: string | null
  createdAt: string
}

export type AdminSubscriptionItem = {
  id: number
  userId: number
  username: string | null
  account: string | null
  productCode: string
  productName: string
  payAmount: string
  status: string
  lastPaidAt: string | null
  nextPaymentAt: string | null
  failReason: string | null
  createdAt: string
}

export type AdminPaymentMethodItem = {
  id: number
  userId: number
  username: string | null
  account: string | null
  cardNo: string | null
  cardName: string | null
  cardCode: string | null
  isMain: boolean | number
  hasBillKey: boolean
  createdAt: string
  updatedAt: string | null
}

export type AdminBillingInviteItem = {
  id: number
  productCode: string
  productName: string
  payAmount: string
  expiresAt: string
  claimedAt: string | null
  claimedByUserId: number | null
  usedAt: string | null
  usedByUserId: number | null
  usedByUsername: string | null
  usedByAccount: string | null
  revokedAt: string | null
  createdAt: string
}

export type CreatedBillingInvite = {
  id: number
  token: string
  productCode: string
  productName: string
  payAmount: string
  expiresAt: string
}

export type AdminPaginatedResult<T> = {
  items: T[]
  total: number
  page: number
  limit: number
}

export const getAdminBillingHistory = async (page = 1, limit = 20, filter = 'all') => {
  try {
    const response = await api.get<AdminPaginatedResult<AdminBillingHistoryItem>>(
      '/api/payment/nano/admin/billing/history',
      { params: { page, limit, filter } }
    )
    return response
  } catch (error) {
    console.error('Error(getAdminBillingHistory): ', error)
    throw error
  }
}

export const getAdminSubscriptions = async (page = 1, limit = 20, filter = 'all') => {
  try {
    const response = await api.get<AdminPaginatedResult<AdminSubscriptionItem>>(
      '/api/payment/nano/admin/billing/subscriptions',
      { params: { page, limit, filter } }
    )
    return response
  } catch (error) {
    console.error('Error(getAdminSubscriptions): ', error)
    throw error
  }
}

export const adminCancelSubscription = async (id: number) => {
  try {
    const response = await api.post<CommonResponse>(
      `/api/payment/nano/admin/billing/subscriptions/${id}/cancel`,
      { id }
    )
    return response
  } catch (error) {
    console.error('Error(adminCancelSubscription): ', error)
    throw error
  }
}

export const getAdminPaymentMethods = async (page = 1, limit = 20) => {
  try {
    const response = await api.get<AdminPaginatedResult<AdminPaymentMethodItem>>(
      '/api/payment/nano/admin/billing/payment-methods',
      { params: { page, limit } }
    )
    return response
  } catch (error) {
    console.error('Error(getAdminPaymentMethods): ', error)
    throw error
  }
}

export const createAdminBillingInvite = async (
  productCode: 'TRADING_ADV_110000' | 'TRADING_ADV_220000',
  expiresInDays: number
) => {
  try {
    return await api.post<CreatedBillingInvite>('/api/payment/nano/admin/billing/invites', {
      productCode,
      expiresInDays
    })
  } catch (error) {
    console.error('Error(createAdminBillingInvite): ', error)
    throw error
  }
}

export const getAdminBillingInvites = async (limit = 30) => {
  try {
    return await api.get<AdminBillingInviteItem[]>('/api/payment/nano/admin/billing/invites', {
      params: { limit }
    })
  } catch (error) {
    console.error('Error(getAdminBillingInvites): ', error)
    throw error
  }
}

export const revokeAdminBillingInvite = async (id: number) => {
  try {
    return await api.post(`/api/payment/nano/admin/billing/invites/${id}/revoke`, {})
  } catch (error) {
    console.error('Error(revokeAdminBillingInvite): ', error)
    throw error
  }
}

type KakaoParams = {
  code: string
  redirectUri?: string
}
// ANCHOR 카카오
export const kakao = async (params: KakaoParams) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/kakao', params)
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ANCHOR 카카오
export const getUser = async () => {
  return api.get<CommonResponse>('/api/main/getUser')
}

// ANCHOR 로그아웃
export const logout = async () => {
  try {
    const response = await api.delete<CommonResponse>('/api/main/logout')
    return response
  } catch (error) {
    console.error('Error during logout', error)
    throw error
  }
}

// ANCHOR 강좌목록조회
export const getCourseList = async (params?: { catalog?: CourseCatalog }) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getCourseList', { params })
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

export type TradingSimLessonItem = {
  id: number
  courseId: number
  title: string
  content: string | null
  thumbnail?: string | null
  duration: number | null
  order: number
  filmedAt?: string | null
  lessonType?: '인사이트' | '트레이딩' | '스윙' | null
  createdAt: string
  validFrom?: string | null
  validUntil?: string | null
}

export type TradingSimCourseInfo = {
  id: number
  title: string
  thumbnail: string | null
  shortDescription?: string | null
  description?: string | null
  accessUntil?: string | null
  label?: string
}

export type TradingSimLessonsResponse = {
  hasAccess: boolean
  course: TradingSimCourseInfo | null
  lessons: TradingSimLessonItem[]
}

// ANCHOR 트심 레슨 목록 조회
export const getTradingSimLessons = async () => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getTradingSimLessons')
    return response
  } catch (error) {
    console.error('Error(getTradingSimLessons): ', error)
    throw error
  }
}

export type TheorySpecialCourseItem = TradingSimCourseInfo & {
  groupId: number | null
  label: string
  hasAccess?: boolean
  accessUntil?: string | null
}

// ANCHOR 이론특강 접근 가능 강좌 목록 조회
export const getTheorySpecialCourses = async () => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getTheorySpecialCourses')
    return response
  } catch (error) {
    console.error('Error(getTheorySpecialCourses): ', error)
    throw error
  }
}

// ANCHOR 이론특강 강의 목록 조회
export const getTheorySpecialLessons = async (params: { courseId: number | string }) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getTheorySpecialLessons', {
      params
    })
    return response
  } catch (error) {
    console.error('Error(getTheorySpecialLessons): ', error)
    throw error
  }
}

// ANCHOR 전반종지 접근 권한 조회
export const getJeonbanJongjiAccess = async () => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getJeonbanJongjiAccess')
    return response
  } catch (error) {
    console.error('Error(getJeonbanJongjiAccess): ', error)
    throw error
  }
}

// ANCHOR 후반종지 접근 권한 조회
export const getHubanJongjiAccess = async () => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getHubanJongjiAccess')
    return response
  } catch (error) {
    console.error('Error(getHubanJongjiAccess): ', error)
    throw error
  }
}

// ANCHOR 강좌조회
type GetCourseParams = {
  id: string
}
export const getCourse = async (params: GetCourseParams) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getCourse', {
      params
    })
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ANCHOR 강의조회
type GetLessonParams = {
  id: string
}
export const getLesson = async (params: GetLessonParams) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getLesson', {
      params
    })
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ANCHOR 수강신청 (일반 사용자용)
type ApplicationForCourseParams = {
  courseId: number
  name: string
  phone: string
  birthDate: string
  gender: string
  address: string
}
export const applicationForCourse = async (
  params: ApplicationForCourseParams
) => {
  try {
    const response = await api.post<CommonResponse>(
      '/api/main/applicationForCourse',
      params
    )
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ANCHOR 수강신청 (관리자용)
type AdminApplicationForCourseParams = {
  userId: number
  courseId: number
  name: string
  phone: string
  birthDate: string
  gender: string
  address: string
  accessUntil?: string
}
export const adminApplicationForCourse = async (
  params: AdminApplicationForCourseParams
) => {
  try {
    const response = await api.post<CommonResponse>(
      '/api/main/applicationForCourse',
      params
    )
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ANCHOR 최근 수강신청
export const recentService = async () => {
  try {
    const response = await api.get<CommonResponse>('/api/main/recentService')
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ANCHOR 누적시청시간
type TotalWatchTimeParams = {
  lessonId: string
  seconds?: number
}
export const totalWatchTime = async (params: TotalWatchTimeParams) => {
  return api.put<CommonResponse>('/api/main/totalWatchTime', params)
}

// ANCHOR 코스별 누적 시청시간
export const getCourseProgress = async () => {
  try {
    const response = await api.get<CommonResponse>(
      '/api/main/getCourseProgress'
    )
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

export type LessonProgressRow = {
  lessonId: number
  title: string
  duration: number | null
  lessonOrder: number
  watchedSeconds: number
  lastLearnedAt?: string | null
}

export type AdminLearningAccessStatus = 'active' | 'expired' | 'history'
export type AdminLearningAccessType = 'public' | 'course' | 'group' | 'account' | 'history'

export type AdminUserLearningCourse = {
  id: number
  title: string
  shortDescription: string | null
  thumbnail: string | null
  catalogs: string[]
  groupId: number | null
  lessonCount: number
  trackableLessonCount: number
  totalDuration: number
  watchedSeconds: number
  cappedWatchedSeconds: number
  startedLessons: number
  completedLessons: number
  totalCompletions: number
  repeatCompletions: number
  lastLearnedAt: string | null
  progressPercent: number
  isCompleted: boolean
  isActive: boolean
  accessStatus: AdminLearningAccessStatus
  accessType: AdminLearningAccessType
  accessLabel: string
  accessUntil: string | null
}

export type AdminUserLearningSummary = {
  user: {
    id: number
    account: string
    userName: string
    username: string | null
    name: string | null
    phone: string | null
    email: string | null
    profileImage: string | null
  }
  overview: {
    activeCourses: number
    historyCourses: number
    startedCourses: number
    completedCourses: number
    watchedSeconds: number
    totalCompletions: number
    repeatCompletions: number
    lastLearnedAt: string | null
  }
  courses: AdminUserLearningCourse[]
}

export const getLessonProgress = async (params: {
  courseId: number | string
}) => {
  try {
    const response = await api.get<CommonResponse>(
      '/api/main/getLessonProgress',
      { params }
    )
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ANCHOR 관리자용 사용자 코스별 진도율 조회
export const getUserCourseProgress = async (params: {
  userId: number | string
}) => {
  try {
    const response = await api.get<CommonResponse>(
      '/api/main/getUserCourseProgress',
      { params }
    )
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ANCHOR 관리자용 사용자 레슨별 진도율 조회
export const getUserLessonProgress = async (params: {
  userId: number | string
  courseId: number | string
}) => {
  try {
    const response = await api.get<CommonResponse>(
      '/api/main/getUserLessonProgress',
      { params }
    )
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ANCHOR 특정 사용자의 서비스 목록 조회
export const getUserServiceList = async (params: { userId: number }) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getServiceList', {
      params: { ...params, pageSize: 100 }
    })
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ============================
// ANCHOR 댓글 관리 API (관리자)
// ============================

// ANCHOR 댓글 목록 조회 (관리자용)
type GetAdminCommentListParams = {
  page?: number
  pageSize?: number
  keyword?: string
  courseId?: number
  lessonId?: number
}
export const getAdminCommentList = async (params: GetAdminCommentListParams) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/admin/comments', {
      params
    })
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ANCHOR 댓글 통계 조회 (관리자용)
export const getCommentStats = async () => {
  try {
    const response = await api.get<CommonResponse>('/api/main/admin/comments/stats')
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ANCHOR 댓글 삭제 (관리자용)
export const deleteAdminComment = async (commentId: number) => {
  try {
    const response = await api.delete<CommonResponse>('/api/main/admin/comments', {
      params: { commentId }
    })
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ============================
// ANCHOR 사용자 정보 관리 API (관리자)
// ============================

// ANCHOR 사용자 정보 수정 (일반 사용자용)
type UpdateMyInfoParams = {
  name: string
  phone: string
  birthDate: string
  gender: string
  address: string
}
export const updateMyInfo = async (params: UpdateMyInfoParams) => {
  try {
    const response = await api.put<CommonResponse>('/api/main/user/info', params)
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ANCHOR 사용자 정보 수정 (관리자용)
type UpdateUserInfoParams = {
  userId: number
  name: string
  phone: string
  birthDate?: string | null
  gender?: string | null
  address?: string | null
}
export const updateUserInfo = async (params: UpdateUserInfoParams) => {
  try {
    const response = await api.put<CommonResponse>('/api/main/admin/user/info', params)
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ANCHOR 회원리스트조회[관리자기능]
export const getUserList = async (params: any) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getUserList', {
      params
    })
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ============================
// ANCHOR 레슨 댓글 관련
// ============================

export type LessonCommentItem = {
  id: number
  lessonId: number
  userId: number
  content: string
  likeCount: number
  isPinned: boolean
  createdAt: string
  updatedAt: string | null
  isMine: boolean
  isLiked: boolean
  author?: {
    id: number
    username: string | null
    profileImage: string | null
    account: string | null
  }
}

export type LessonCommentList = {
  items: LessonCommentItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ANCHOR 레슨 댓글 작성
export const createLessonComment = async (params: {
  lessonId: number
  content: string
}) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/create', params)
    return response
  } catch (error) {
    console.error('Error(createLessonComment): ', error)
    throw error
  }
}

// ANCHOR 레슨 댓글 목록
export const getLessonComments = async (params: {
  lessonId: number | string
  page?: number
  pageSize?: number
  order?: 'ASC' | 'DESC'
}) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/list', {
      params
    })
    return response
  } catch (error) {
    console.error('Error(getLessonComments): ', error)
    throw error
  }
}

// ANCHOR 댓글 삭제(본인/관리자)
export const deleteComment = async (params: { commentId: number }) => {
  try {
    const response = await api.delete<CommonResponse>('/api/main/delete', {
      params
    })
    return response
  } catch (error) {
    console.error('Error(deleteComment): ', error)
    throw error
  }
}

// ANCHOR 댓글 좋아요
export const likeComment = async (params: { commentId: number }) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/like', params)
    return response
  } catch (error) {
    console.error('Error(likeComment): ', error)
    throw error
  }
}

// ANCHOR 댓글 좋아요 취소
export const unlikeComment = async (params: { commentId: number }) => {
  try {
    const response = await api.delete<CommonResponse>('/api/main/unlike', {
      params
    })
    return response
  } catch (error) {
    console.error('Error(unlikeComment): ', error)
    throw error
  }
}

// ANCHOR 유료회원 기간 설정[관리자기능]
export const setPremiumPeriod = async (params: {
  userId: number
  premiumUntil: string
}) => {
  try {
    const response = await api.post<CommonResponse>(
      '/api/main/setPremiumPeriod',
      params
    )
    return response
  } catch (error) {
    console.error('Error(setPremiumPeriod): ', error)
    throw error
  }
}

// ANCHOR 무료회원으로 강등[관리자기능]
export const removePremium = async (params: {
  userId: number
}) => {
  try {
    const response = await api.post<CommonResponse>(
      '/api/main/removePremium',
      params
    )
    return response
  } catch (error) {
    console.error('Error(removePremium): ', error)
    throw error
  }
}

// ANCHOR 회원 데이터 엑셀 다운로드
export const exportUsersExcel = async (params?: {
  keyword?: string
}) => {
  try {
    const response = await api.get('/api/main/exportUsersExcel', {
      params,
      responseType: 'blob'
    })
    return response
  } catch (error) {
    console.error('Error(exportUsersExcel): ', error)
    throw error
  }
}

// ANCHOR 회원탈퇴[관리자기능]
export const deleteUser = async (params: {
  userId: number
}) => {
  try {
    const response = await api.delete<CommonResponse>(
      '/api/main/deleteUser',
      { data: params }
    )
    return response
  } catch (error) {
    console.error('Error(deleteUser): ', error)
    throw error
  }
}

// ============================
// ANCHOR 주식 리포트
// ============================

export type StockReportItem = {
  id: number
  stockSymbol: string
  stockName: string
  content: string | null
  status?: 'pending' | 'processing' | 'completed' | 'failed'
  createdAt: string
  isOwnReport: boolean
  shared?: boolean
}

export const getReport = async (params: { stockSymbol: string }) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getReport', {
      params: { stockSymbol: params.stockSymbol }
    })
    return response
  } catch (error) {
    console.error('Error(getReport): ', error)
    throw error
  }
}

export const createReport = async (params: {
  stockSymbol: string
  stockName: string
}) => {
  try {
    // 비동기 처리로 변경되어 즉시 응답 반환 (타임아웃 제거)
    const response = await api.post<CommonResponse>('/api/main/createReport', params, {
      timeout: 10000 // 10초 (초기 응답만 받으면 됨)
    })
    return response
  } catch (error) {
    console.error('Error(createReport): ', error)
    throw error
  }
}

export type TodayReportItem = {
  id: number
  stockSymbol: string
  stockName: string
  content: string | null
  status?: 'pending' | 'processing' | 'completed' | 'failed'
  createdAt: string
  user: {
    id: number
    username: string
    profileImage: string | null
  } | null
}

export type TodayReportsList = {
  items: TodayReportItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export const getTodayReports = async (params?: {
  page?: number
  pageSize?: number
}) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getTodayReports', {
      params: {
        page: params?.page || 1,
        pageSize: params?.pageSize || 20
      }
    })
    return response
  } catch (error) {
    console.error('Error(getTodayReports): ', error)
    throw error
  }
}

// ============================
// ANCHOR 주식 데이터 조회
// ============================

// ANCHOR 이미지 업로드
export const uploadImage = async (file: File) => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await api.post<CommonResponse>('/api/main/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response
  } catch (error) {
    console.error('Error(uploadImage): ', error)
    throw error
  }
}

// ============================
// ANCHOR 라이브 채팅 API
// ============================

export type LiveChatMessage = {
  id: number
  liveId: number
  userId: number
  message: string
  createdAt: string
  user: {
    id: number
    name: string
    username: string
    account: string
    profileImage: string | null
    isAdmin: number
  }
}

export type LiveChatList = {
  items: LiveChatMessage[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ANCHOR 라이브 채팅 메시지 목록 조회
export const getLiveChatMessages = async (params: {
  liveId: number
  page?: number
  pageSize?: number
}) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/live/chat/messages', {
      params
    })
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

// ============================
// ANCHOR 로그인 이력 관리 API
// ============================

export type UserLoginHistoryItem = {
  id: number
  userId: number
  type: number // 1: 로그인, 0: 로그아웃
  ipAddress: string | null
  createdAt: string
  user: {
    id: number
    account: string
    username: string
  } | null
}

export type UserLoginHistoryList = {
  items: UserLoginHistoryItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
  stats: {
    total: number
    login: number
    logout: number
  }
}

// ANCHOR 일반 사용자 로그인 이력 목록 조회
export const getUserLoginHistoryList = async (params?: {
  page?: number
  pageSize?: number
  account?: string
  type?: string
  createdStartAt?: string
  createdEndAt?: string
}) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/admin/user-login-history', {
      params: {
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
        ...(params?.account && { account: params.account }),
        ...(params?.type && { type: params.type }),
        ...(params?.createdStartAt && { createdStartAt: params.createdStartAt }),
        ...(params?.createdEndAt && { createdEndAt: params.createdEndAt })
      }
    })
    return response
  } catch (error) {
    console.error('Error(getUserLoginHistoryList): ', error)
    throw error
  }
}

// ============================
// ANCHOR 커뮤니티 API
// ============================

export type PopupNoticeItem = {
  id: number
  title: string
  content: string | null
  imageUrl: string | null
  linkUrl: string | null
  isActive: number
  order: number
  startAt: string | null
  endAt: string | null
  createdAt: string | null
  updatedAt: string | null
  deletedAt: string | null
}

export type PopupNoticeList = {
  items: PopupNoticeItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export const getPopupNotices = async () => {
  try {
    const response = await api.get<CommonResponse>('/api/main/popup-notices')
    return response
  } catch (error) {
    console.error('Error(getPopupNotices): ', error)
    throw error
  }
}

export const getAdminPopupNoticeList = async (params?: {
  page?: number
  pageSize?: number
  keyword?: string
}) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/admin/popup-notices', { params })
    return response
  } catch (error) {
    console.error('Error(getAdminPopupNoticeList): ', error)
    throw error
  }
}

export const getAdminPopupNotice = async (id: number) => {
  try {
    const response = await api.get<CommonResponse>(`/api/main/admin/popup-notices/${id}`)
    return response
  } catch (error) {
    console.error('Error(getAdminPopupNotice): ', error)
    throw error
  }
}

export const createPopupNotice = async (params: {
  title: string
  content?: string
  imageUrl?: string
  linkUrl?: string
  isActive?: boolean
  order?: number
  startAt?: string
  endAt?: string
}) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/admin/popup-notices', params)
    return response
  } catch (error) {
    console.error('Error(createPopupNotice): ', error)
    throw error
  }
}

export const updatePopupNotice = async (params: {
  id: number
  title: string
  content?: string
  imageUrl?: string
  linkUrl?: string
  isActive?: boolean
  order?: number
  startAt?: string
  endAt?: string
}) => {
  try {
    const response = await api.put<CommonResponse>('/api/main/admin/popup-notices', params)
    return response
  } catch (error) {
    console.error('Error(updatePopupNotice): ', error)
    throw error
  }
}

export const deletePopupNotice = async (params: { id: number }) => {
  try {
    const response = await api.delete<CommonResponse>('/api/main/admin/popup-notices', { params })
    return response
  } catch (error) {
    console.error('Error(deletePopupNotice): ', error)
    throw error
  }
}

export type CommunityItem = {
  id: number
  userId: number
  title: string
  content: string
  category: 'notice' | 'qna' | 'free'
  image1: string | null
  image2: string | null
  image3: string | null
  viewCount: number
  likeCount: number
  createdAt: Date
  updatedAt: Date | null
  user: {
    id: number
    account: string
    username: string
    profileImage: string | null
  } | null
}

export type CommunityList = {
  items: CommunityItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export type CommunityDetail = {
  id: number
  userId: number
  title: string
  content: string
  category: 'notice' | 'qna' | 'free'
  image1: string | null
  image2: string | null
  image3: string | null
  viewCount: number
  likeCount: number
  createdAt: Date
  updatedAt: Date | null
  user: {
    id: number
    account: string
    username: string
    profileImage: string | null
  } | null
  isLiked: boolean
}

export type CommunityCommentItem = {
  id: number
  communityId: number
  userId: number
  parentId: number | null
  content: string
  likeCount: number
  createdAt: Date
  updatedAt: Date | null
  isMine: boolean
  isLiked: boolean
  author: {
    id: number
    account: string
    username: string
    profileImage: string | null
  } | null
  replies: CommunityCommentItem[]
}

export type CommunityCommentList = {
  items: CommunityCommentItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ANCHOR 커뮤니티 목록 조회
export const getCommunityList = async (params?: {
  page?: number
  pageSize?: number
  keyword?: string
  category?: 'notice' | 'qna' | 'free'
  createdStartAt?: string
  createdEndAt?: string
}) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/community/list', {
      params: {
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
        ...(params?.keyword && { keyword: params.keyword }),
        ...(params?.category && { category: params.category }),
        ...(params?.createdStartAt && { createdStartAt: params.createdStartAt }),
        ...(params?.createdEndAt && { createdEndAt: params.createdEndAt })
      }
    })
    return response
  } catch (error) {
    console.error('Error(getCommunityList): ', error)
    throw error
  }
}

// ANCHOR 커뮤니티 상세 조회
export const getCommunity = async (id: number) => {
  try {
    const response = await api.get<CommonResponse>(`/api/main/community/${id}`)
    return response
  } catch (error) {
    console.error('Error(getCommunity): ', error)
    throw error
  }
}

// ANCHOR 커뮤니티 생성
export const createCommunity = async (params: {
  title: string
  content: string
  category?: 'notice' | 'qna' | 'free'
  image1?: string
  image2?: string
  image3?: string
}) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/community', params)
    return response
  } catch (error) {
    console.error('Error(createCommunity): ', error)
    throw error
  }
}

// ANCHOR 커뮤니티 수정
export const updateCommunity = async (params: {
  id: number
  title?: string
  content?: string
  category?: 'notice' | 'qna' | 'free'
  image1?: string | null
  image2?: string | null
  image3?: string | null
}) => {
  try {
    const response = await api.put<CommonResponse>('/api/main/community', params)
    return response
  } catch (error) {
    console.error('Error(updateCommunity): ', error)
    throw error
  }
}

// ANCHOR 커뮤니티 삭제
export const deleteCommunity = async (id: number) => {
  try {
    const response = await api.delete<CommonResponse>('/api/main/community', {
      params: { id }
    })
    return response
  } catch (error) {
    console.error('Error(deleteCommunity): ', error)
    throw error
  }
}

export const adminCreateCommunity = async (params: {
  title: string
  content: string
  category?: 'notice' | 'qna' | 'free'
  image1?: string
  image2?: string
  image3?: string
}) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/admin/community', params)
    return response
  } catch (error) {
    console.error('Error(adminCreateCommunity): ', error)
    throw error
  }
}

export const adminUpdateCommunity = async (params: {
  id: number
  title?: string
  content?: string
  category?: 'notice' | 'qna' | 'free'
  image1?: string | null
  image2?: string | null
  image3?: string | null
}) => {
  try {
    const response = await api.put<CommonResponse>('/api/main/admin/community', params)
    return response
  } catch (error) {
    console.error('Error(adminUpdateCommunity): ', error)
    throw error
  }
}

export const adminDeleteCommunity = async (id: number) => {
  try {
    const response = await api.delete<CommonResponse>('/api/main/admin/community', {
      params: { id }
    })
    return response
  } catch (error) {
    console.error('Error(adminDeleteCommunity): ', error)
    throw error
  }
}

export const adminCreateCommunityAnswer = async (params: {
  communityId: number
  content: string
}) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/admin/community/answer', params)
    return response
  } catch (error) {
    console.error('Error(adminCreateCommunityAnswer): ', error)
    throw error
  }
}

// ANCHOR 커뮤니티 좋아요 토글
export const toggleCommunityLike = async (communityId: number) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/community/like', {
      communityId
    })
    return response
  } catch (error) {
    console.error('Error(toggleCommunityLike): ', error)
    throw error
  }
}

// ANCHOR 커뮤니티 댓글 목록 조회
export const getCommunityComments = async (params: {
  communityId: number
  page?: number
  pageSize?: number
}) => {
  try {
    const response = await api.get<CommonResponse>(
      `/api/main/community/${params.communityId}/comments`,
      {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20
        }
      }
    )
    return response
  } catch (error) {
    console.error('Error(getCommunityComments): ', error)
    throw error
  }
}

// ANCHOR 커뮤니티 댓글 생성
export const createCommunityComment = async (params: {
  communityId: number
  parentId?: number
  content: string
}) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/community/comment', params)
    return response
  } catch (error) {
    console.error('Error(createCommunityComment): ', error)
    throw error
  }
}

// ANCHOR 커뮤니티 댓글 좋아요 토글
export const toggleCommunityCommentLike = async (commentId: number) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/community/comment/like', {
      commentId
    })
    return response
  } catch (error) {
    console.error('Error(toggleCommunityCommentLike): ', error)
    throw error
  }
}

// ANCHOR 커뮤니티 댓글 삭제
export const deleteCommunityComment = async (commentId: number) => {
  try {
    const response = await api.delete<CommonResponse>('/api/main/community/comment', {
      params: { commentId }
    })
    return response
  } catch (error) {
    console.error('Error(deleteCommunityComment): ', error)
    throw error
  }
}

// ANCHOR 커뮤니티 통계 조회 (관리자용)
export type CommunityStats = {
  totalPosts: number
  totalComments: number
  totalReplies: number
  totalLikes: number
  totalViews: number
}

export type WatchTimeRankingItem = {
  id: number
  account: string
  username: string | null
  name: string | null
  email: string | null
  phone: string | null
  profileImage: string | null
  isAdmin: number | null
  createdAt: string
  totalWatchedSeconds: number
}

export type WatchTimeRankingList = {
  items: WatchTimeRankingItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ANCHOR 시청 시간 순위 조회 (관리자용)
export const getWatchTimeRanking = async (params?: {
  page?: number
  pageSize?: number
  keyword?: string
  period?: 'all' | 'weekly' | 'monthly' | 'yearly'
}) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getWatchTimeRanking', {
      params: {
        page: params?.page,
        pageSize: params?.pageSize,
        keyword: params?.keyword,
        period: params?.period
      }
    })
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

export type DashboardStats = {
  overview: {
    totalUsers: number
    activeLearners: number
    learnersLast7Days: number
    watchSecondsLast7Days: number
    todayLogins: number
  }
  operations: {
    pendingApplications: number
    expiringWithin7Days: number
    expiringWithin30Days: number
    inactiveLearners: number
    todayLives: number
  }
  lists: {
    pendingApplications: Array<{
      id: number
      userId: number
      userName: string
      courseId: number
      courseTitle: string
      createdAt: string | null
    }>
    expiringAccesses: Array<{
      accessType: 'course' | 'group' | 'account'
      id: number
      userId: number
      userName: string
      userAccount: string
      accessName: string
      accessUntil: string
      daysRemaining: number
      groupId: number | null
      courseId: number | null
    }>
    inactiveLearners: Array<{
      userId: number
      userName: string
      userAccount: string
      activeAccessCount: number
      lastLearnedAt: string | null
      daysInactive: number | null
    }>
    todayLives: Array<{
      id: number
      title: string
      status: 'scheduled' | 'live'
      scheduledStartAt: string
      groupId: number | null
      groupName: string | null
    }>
  }
}

export const getAdminUserLearningSummary = async (userId: number | string) => {
  return api.get<CommonResponse>(`/api/main/admin/users/${userId}/learning-summary`)
}

// ANCHOR 대시보드 통계 조회 (관리자용)
export const getDashboardStats = async (params?: {
  startDate?: string
  endDate?: string
}) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getDashboardStats', {
      params: {
        startDate: params?.startDate,
        endDate: params?.endDate
      }
    })
    return response
  } catch (error) {
    console.error('Error: ', error)
    throw error
  }
}

export const getCommunityStats = async () => {
  try {
    const response = await api.get<CommonResponse>('/api/main/admin/community/stats')
    return response
  } catch (error) {
    console.error('Error(getCommunityStats): ', error)
    throw error
  }
}

// ============================
// ANCHOR 퀴즈 관련
// ============================

export type QuizItem = {
  id: number
  title: string
  description: string | null
  startDate: string
  endDate: string
  isActive: number
  createdAt: string
  updatedAt: string | null
}

export type QuizQuestionItem = {
  id: number
  quizId: number
  type: 'CHOICE_4' | 'OX' | 'ESSAY'
  content: string
  image1: string | null
  image2: string | null
  image3: string | null
  image4: string | null
  image5: string | null
  correctAnswer: string | null
  order: number
  options?: { id: number; optionNumber: number; content: string }[]
  myResponse?: { id: number; answer: string; isCorrect: number | null } | null
}

// ANCHOR 퀴즈 목록 조회 (사용자용)
export const getQuizList = async (params?: {
  page?: number
  pageSize?: number
}) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getQuizList', { params })
    return response
  } catch (error) {
    console.error('Error(getQuizList): ', error)
    throw error
  }
}

// ANCHOR 퀴즈 목록 조회 (관리자용)
export const getAdminQuizList = async (params?: {
  page?: number
  pageSize?: number
  keyword?: string
}) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getAdminQuizList', { params })
    return response
  } catch (error) {
    console.error('Error(getAdminQuizList): ', error)
    throw error
  }
}

// ANCHOR 퀴즈 상세 조회
export const getQuiz = async (params: { id: number }) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getQuiz', { params })
    return response
  } catch (error) {
    console.error('Error(getQuiz): ', error)
    throw error
  }
}

// ANCHOR 퀴즈 생성 (관리자)
export const createQuiz = async (params: {
  title: string
  description?: string
  startDate: string
  endDate: string
  isActive?: number
}) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/createQuiz', params)
    return response
  } catch (error) {
    console.error('Error(createQuiz): ', error)
    throw error
  }
}

// ANCHOR 퀴즈 수정 (관리자)
export const updateQuiz = async (params: {
  id: number
  title?: string
  description?: string
  startDate?: string
  endDate?: string
  isActive?: number
}) => {
  try {
    const response = await api.put<CommonResponse>('/api/main/updateQuiz', params)
    return response
  } catch (error) {
    console.error('Error(updateQuiz): ', error)
    throw error
  }
}

// ANCHOR 퀴즈 삭제 (관리자)
export const deleteQuiz = async (params: { id: number }) => {
  try {
    const response = await api.delete<CommonResponse>('/api/main/deleteQuiz', { params })
    return response
  } catch (error) {
    console.error('Error(deleteQuiz): ', error)
    throw error
  }
}

// ANCHOR 문제 생성 (관리자)
export const createQuizQuestion = async (params: {
  quizId: number
  type: 'CHOICE_4' | 'OX' | 'ESSAY'
  content: string
  image1?: string
  image2?: string
  image3?: string
  image4?: string
  image5?: string
  correctAnswer?: string
  order?: number
  options?: { optionNumber: number; content: string }[]
}) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/createQuizQuestion', params)
    return response
  } catch (error) {
    console.error('Error(createQuizQuestion): ', error)
    throw error
  }
}

// ANCHOR 문제 수정 (관리자)
export const updateQuizQuestion = async (params: {
  id: number
  type?: 'CHOICE_4' | 'OX' | 'ESSAY'
  content?: string
  image1?: string
  image2?: string
  image3?: string
  image4?: string
  image5?: string
  correctAnswer?: string
  order?: number
  options?: { optionNumber: number; content: string }[]
}) => {
  try {
    const response = await api.put<CommonResponse>('/api/main/updateQuizQuestion', params)
    return response
  } catch (error) {
    console.error('Error(updateQuizQuestion): ', error)
    throw error
  }
}

// ANCHOR 문제 삭제 (관리자)
export const deleteQuizQuestion = async (params: { id: number }) => {
  try {
    const response = await api.delete<CommonResponse>('/api/main/deleteQuizQuestion', { params })
    return response
  } catch (error) {
    console.error('Error(deleteQuizQuestion): ', error)
    throw error
  }
}

// ANCHOR 퀴즈 응답 제출
export const submitQuizResponse = async (params: {
  quizId: number
  answers: { questionId: number; answer: string }[]
}) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/submitQuizResponse', params)
    return response
  } catch (error) {
    console.error('Error(submitQuizResponse): ', error)
    throw error
  }
}

// ANCHOR 퀴즈 결과 조회
export const getQuizResult = async (params: { quizId: number }) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getQuizResult', { params })
    return response
  } catch (error) {
    console.error('Error(getQuizResult): ', error)
    throw error
  }
}

// ANCHOR 퀴즈 응답 목록 조회 (관리자용)
export const getQuizResponses = async (params: {
  quizId: number
  page?: number
  pageSize?: number
}) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getQuizResponses', { params })
    return response
  } catch (error) {
    console.error('Error(getQuizResponses): ', error)
    throw error
  }
}

// ANCHOR 주관식 채점 (관리자)
export const gradeEssayResponse = async (params: {
  responseId: number
  isCorrect: 0 | 1
}) => {
  try {
    const response = await api.put<CommonResponse>('/api/main/gradeEssayResponse', params)
    return response
  } catch (error) {
    console.error('Error(gradeEssayResponse): ', error)
    throw error
  }
}

// ANCHOR 주관식 응답 상세 목록 조회 (관리자용)
export const getEssayResponses = async (params: { quizId: number }) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/getEssayResponses', { params })
    return response
  } catch (error) {
    console.error('Error(getEssayResponses): ', error)
    throw error
  }
}

// ============================
// ANCHOR 그룹 관리 API
// ============================

export type GroupItem = {
  id: number
  name: string
  description: string | null
  userCount: number
  createdAt: string
  updatedAt: string | null
}

export type GroupList = {
  items: GroupItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export type GroupUserItem = {
  id: number
  account: string
  username: string
  name: string | null
  email: string | null
  phone: string | null
  profileImage: string | null
  accessUntil: string | null
  accessStatus: 'unlimited' | 'valid' | 'expiring' | 'expired'
  assignedAt: string
}

export type GroupUserList = {
  items: GroupUserItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
  counts: {
    all: number
    active: number
    expiring: number
    expired: number
  }
}

export type GroupSelectableUser = {
  id: number
  account: string
  username: string
  name: string | null
  email: string | null
  phone: string | null
  profileImage: string | null
  isMember: number
  accessUntil: string | null
}

export type GroupSelectableUserList = {
  items: GroupSelectableUser[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export type UserGroupAccessItem = {
  id: number
  name: string
  description: string | null
  accessUntil: string | null
  assignedAt: string
  accessStatus: 'unlimited' | 'valid' | 'expired'
}

// ANCHOR 그룹 목록 조회 (관리자용)
export const getGroupList = async (params?: {
  page?: number
  pageSize?: number
  keyword?: string
}) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/admin/groups', {
      params
    })
    return response
  } catch (error) {
    console.error('Error(getGroupList): ', getErrorMessage(error))
    throw error
  }
}

// ANCHOR 그룹 상세 조회
export const getGroup = async (id: number) => {
  try {
    const response = await api.get<CommonResponse>(`/api/main/admin/groups/${id}`)
    return response
  } catch (error) {
    console.error('Error(getGroup): ', getErrorMessage(error))
    throw error
  }
}

// ANCHOR 그룹 생성
export const createGroup = async (params: {
  name: string
  description?: string
}) => {
  try {
    const response = await api.post<CommonResponse>('/api/main/admin/groups', params)
    return response
  } catch (error) {
    console.error('Error(createGroup): ', getErrorMessage(error))
    throw error
  }
}

// ANCHOR 그룹 수정
export const updateGroup = async (params: {
  id: number
  name: string
  description?: string
}) => {
  try {
    const response = await api.put<CommonResponse>('/api/main/admin/groups', params)
    return response
  } catch (error) {
    console.error('Error(updateGroup): ', getErrorMessage(error))
    throw error
  }
}

// ANCHOR 그룹 삭제
export const deleteGroup = async (id: number) => {
  try {
    const response = await api.delete<CommonResponse>('/api/main/admin/groups', {
      params: { id }
    })
    return response
  } catch (error) {
    console.error('Error(deleteGroup): ', getErrorMessage(error))
    throw error
  }
}

// ANCHOR 그룹 사용자 목록 조회
export const getGroupUsers = async (groupId: number, params?: {
  page?: number
  pageSize?: number
  keyword?: string
  accessStatus?: 'all' | 'active' | 'expiring' | 'expired'
}) => {
  try {
    const response = await api.get<CommonResponse>(`/api/main/admin/groups/${groupId}/users`, { params })
    return response
  } catch (error) {
    console.error('Error(getGroupUsers): ', getErrorMessage(error))
    throw error
  }
}

// ANCHOR 그룹 사용자 업데이트
export const updateGroupUsers = async (params: {
  groupId: number
  users: { userId: number; accessUntil?: string | null }[]
}) => {
  try {
    const response = await api.put<CommonResponse>('/api/main/admin/groups/users', params)
    return response
  } catch (error) {
    console.error('Error(updateGroupUsers): ', getErrorMessage(error))
    throw error
  }
}

// ANCHOR 그룹 사용자 변경분 업데이트
export const patchGroupUsers = async (params: {
  groupId: number
  upserts: { userId: number; accessUntil?: string | null }[]
  removedUserIds: number[]
}) => {
  try {
    const response = await api.patch<CommonResponse>('/api/main/admin/groups/users', params)
    return response
  } catch (error) {
    console.error('Error(patchGroupUsers): ', getErrorMessage(error))
    throw error
  }
}

// ANCHOR 그룹 사용자 일괄 권한 부여
export const bulkGrantGroupUsers = async (params: {
  groupId: number
  users: { userId: number; accessUntil?: string | null }[]
}) => {
  try {
    const response = await api.put<CommonResponse>('/api/main/admin/groups/users/bulk-grant', params)
    return response
  } catch (error) {
    console.error('Error(bulkGrantGroupUsers): ', getErrorMessage(error))
    throw error
  }
}

// ANCHOR 전체 사용자 목록 조회 (그룹 선택용)
export const getAllUsersForGroup = async (params: {
  groupId: number
  page?: number
  pageSize?: number
  keyword?: string
  membership?: 'all' | 'nonMembers'
}) => {
  try {
    const response = await api.get<CommonResponse>('/api/main/admin/groups/all-users', { params })
    return response
  } catch (error) {
    console.error('Error(getAllUsersForGroup): ', getErrorMessage(error))
    throw error
  }
}

// ANCHOR 사용자가 속한 그룹 목록 조회
export const getUserGroups = async (userId: number) => {
  try {
    const response = await api.get<CommonResponse>(`/api/main/admin/user/${userId}/groups`)
    return response
  } catch (error) {
    console.error('Error(getUserGroups): ', getErrorMessage(error))
    throw error
  }
}

export const updateUserGroups = async (params: {
  userId: number
  groups: { groupId: number; accessUntil?: string | null }[]
}) => {
  try {
    const response = await api.put<CommonResponse>('/api/main/admin/user/groups', params)
    return response
  } catch (error) {
    console.error('Error(updateUserGroups): ', getErrorMessage(error))
    throw error
  }
}
